/**
 * 이상한 방 - 카탈로그 엔트리: 테이블 · 수납 그룹
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.1절(그리드 해상도) · 4.2절(무변화 회전) · 8절(카탈로그 규격 요건)
 * 계약: `../entryTypes.ts` · 매니페스트: `public/assets/models.json` · 시각 색인: `public/assets/catalog-preview.png`
 *
 * ## 이 그룹이 방에 하는 일
 *
 * 테이블·수납은 **다른 소품이 설 자리를 만드는 가구**다. 상판이 넓은 가구가 없으면
 * `sizeClass: 'surface'` 소품(머그·책·접시)은 전부 바닥에 놓이고, 그 순간 실내는
 * 실내가 아니라 소품을 흩뿌린 격자가 된다. 그래서 이 그룹에서 가장 중요한 값은
 * 이름이나 색이 아니라 **footprint** 이고, 전부 `cellsFromMeters()` 로 계산했다.
 *
 * ## 담당 35종 중 20종만 배선한 이유
 *
 * 종 수를 억지로 채우면 카탈로그는 늘어나도 술래가 보는 화면은 그대로다.
 * 아래 세 가지에 걸린 15종은 **넣지 않는 것이 이 작업의 목적에 부합한다**(하단 SKIPPED 표 참조).
 *
 *  1. 이미 기존 44종이 그 GLB를 쓰고 있다 - 넣으면 같은 물건이 두 이름으로 나타난다.
 *  2. 매니페스트가 `aliasOf` 로 표시한 **바이트 동일 파일**이다(`table` = `table-dining`,
 *     `bookcase-closed` = `shelf-tall`). 둘 다 기존 엔트리가 이미 쓰고 있다.
 *  3. 실루엣(외곽선)이 이미 배선된 변종과 사실상 같다. 판정 기준은 아래 한 줄로 통일했다 -
 *     **"점유 셀과 재질이 둘 다 같으면 같은 물건"**. 서랍 손잡이나 문짝 개수처럼
 *     1인칭 몇 미터 거리에서 사라지는 차이는 난이도가 아니라 짜증이다.
 *
 * ## 회전 대칭 판정 메모 (전부 catalog-preview.png 를 보고 정했다)
 *
 *  - `table-round` 는 이름과 달리 **정육각 상판**이다. 육각형은 180°에서만 겹치므로 2다.
 *    매니페스트도 이를 뒷받침한다 - 실측이 1.386m × 1.6m 로 x·z가 다르고(6칸 × 7칸),
 *    `assertEntryInvariants` 는 정사각이 아닌 footprint에 4를 허용하지 않는다.
 *  - 정사각 커피테이블 2종만 4다. 다리 네 개가 동일하고 상판이 정사각이라 90°가 무변화다.
 *  - 나머지 직사각 테이블은 전부 2, 문·서랍·ㄱ자처럼 앞면이 있는 수납은 전부 1이다.
 */

import type { CatalogEntryDraft } from '../entryTypes'
import { cellsFromMeters } from '../entryTypes'

/* ------------------------------------------------------------------------------------------------
 * 실측값 → footprint
 * ---------------------------------------------------------------------------------------------- */

/**
 * `models.json` 의 `sizeMeters` 배열을 그대로 옮겨 적기 위한 형태.
 *
 * 매니페스트를 import하지 않는 이유는 `entryTypes.ts` 의 `knownModelIds` 주석과 같다 -
 * `public/` 자산이 번들에 들어가면 안 된다. 대신 **배열 그대로**를 받아 셀 변환은
 * 전적으로 `cellsFromMeters()` 에 맡긴다. 사람이 손대는 건 숫자 세 개의 전사(轉寫)뿐이고,
 * 그 세 숫자는 아래 각 엔트리 주석의 modelId 옆에 그대로 남아 있어 매니페스트와 대조할 수 있다.
 */
type SizeMeters = readonly [x: number, y: number, z: number]

/** 매니페스트 `sizeMeters` 튜플을 점유 셀로 바꾼다. x·y·z 순서 사고를 여기 한 곳에 가둔다. */
function footprintOf(sizeMeters: SizeMeters): CatalogEntryDraft['footprint'] {
  return cellsFromMeters({ x: sizeMeters[0], y: sizeMeters[1], z: sizeMeters[2] })
}

/* ------------------------------------------------------------------------------------------------
 * 식탁 · 데스크 - sizeClass 'floor'
 *
 * 테이블은 벽에 붙지 않는다. 사람이 사방에서 앉아야 하므로 방 가운데에 서고,
 * 그래서 전부 'floor' 다(벽에 붙는 건 아래 수납 구간이 담당한다).
 * ---------------------------------------------------------------------------------------------- */

const DINING_TABLES: readonly CatalogEntryDraft[] = [
  {
    id: 'table-round',
    modelId: 'table-round',
    name: '육각 식탁',
    // table-round [1.386, 0.733, 1.6] → 6 × 3 × 7
    footprint: footprintOf([1.386, 0.733, 1.6]),
    // 상판이 원이 아니라 정육각이다. 90° 돌리면 꼭짓점 방향이 바뀌어 확실히 보이고,
    // 180°는 겹친다 → 2. (x 6칸 ≠ z 7칸이라 애초에 4를 선언할 수도 없다)
    rotSymmetry: 2,
    sizeClass: 'floor',
    // 단일 슬롯 모델. 상판·기둥·받침이 전부 이 하나라 여기가 곧 주된 색면이다.
    recolorSlot: 'wood',
    tags: ['kitchen', 'living'],
    weight: 4,
    defaultColorId: 5,
  },
  {
    id: 'table-glass',
    modelId: 'table-glass',
    name: '유리 식탁',
    // table-glass [1.683, 0.653, 0.895] → 7 × 3 × 4
    footprint: footprintOf([1.683, 0.653, 0.895]),
    // 직사각 상판 + 네 다리. 180° 무변화, 90°는 가로세로가 바뀌어 확연하다.
    rotSymmetry: 2,
    sizeClass: 'floor',
    // 상판(glass)이 위에서 보는 유일한 큰 색면이다. 프레임(metal)을 칠하면 다리만 굵어 보인다.
    recolorSlot: 'glass',
    // 기존 식탁과 점유 셀은 같지만 상판이 투과되어 1인칭에서 즉시 갈린다.
    tags: ['living', 'kitchen'],
    weight: 3,
    defaultColorId: 9,
  },
  {
    id: 'table-cross',
    modelId: 'table-cross',
    name: 'X다리 식탁',
    // table-cross [1.704, 0.695, 0.895] → 7 × 3 × 4
    footprint: footprintOf([1.704, 0.695, 0.895]),
    // 상판은 직사각, 다리는 양 끝 X자 트레슬 두 짝 → 앞뒤 대칭이라 2.
    rotSymmetry: 2,
    sizeClass: 'floor',
    recolorSlot: 'wood',
    // 다리가 옆으로 벌어져 벽에 못 붙인다 → 복도 가운데 놓여도 어색하지 않다.
    tags: ['kitchen', 'living', 'hall'],
    weight: 4,
    defaultColorId: 5,
  },
  {
    id: 'table-cloth',
    modelId: 'table-cloth',
    name: '천 덮인 식탁',
    // table-cloth [1.683, 0.653, 0.895] → 7 × 3 × 4
    footprint: footprintOf([1.683, 0.653, 0.895]),
    // 러너가 상판 가운데를 가로지르고 양 끝으로 똑같이 늘어진다 → 180° 무변화.
    rotSymmetry: 2,
    sizeClass: 'floor',
    // 러너(carpet)가 이 소품의 정체성이자 위에서 보이는 가장 큰 색면이다.
    // 몸통(wood)을 칠하면 다른 나무 식탁과 구별이 사라진다.
    recolorSlot: 'carpet',
    tags: ['kitchen', 'living'],
    weight: 3,
    defaultColorId: 7,
  },
  {
    id: 'desk-corner',
    modelId: 'desk-corner',
    name: 'ㄱ자 책상',
    // desk-corner [1.949, 0.769, 1.949] → 8 × 4 × 8
    footprint: footprintOf([1.949, 0.769, 1.949]),
    // ㄱ자라서 90°마다 빈 사분면이 옮겨 간다 → 네 방향이 전부 구별된다.
    // 점유 셀이 정사각(8 × 8)이라 눈대중으로는 4로 착각하기 쉬운 대표 함정이다.
    rotSymmetry: 1,
    sizeClass: 'anchor',
    // 두 벽에 등을 대야만 형태가 성립한다 → floor가 아니라 anchor.
    recolorSlot: 'wood',
    tags: ['bedroom', 'living'],
    // 2m × 2m 를 잡아먹는다. 흔하게 깔리면 방이 책상으로만 찬다.
    weight: 2,
    defaultColorId: 5,
  },
]

/* ------------------------------------------------------------------------------------------------
 * 커피테이블 - sizeClass 'floor'
 *
 * 네 종을 다 남긴 이유: 서로 **점유 셀이 다르거나(직사각 6칸 ↔ 정사각 4칸)
 * 재질이 다르다(나무 ↔ 유리)**. 어느 두 개를 골라도 한 축은 반드시 갈린다.
 * 상판 높이가 2칸(0.46m)이라 소파 앞 낮은 실루엣을 이 구간이 전담한다.
 * ---------------------------------------------------------------------------------------------- */

const COFFEE_TABLES: readonly CatalogEntryDraft[] = [
  {
    id: 'table-coffee',
    modelId: 'table-coffee',
    name: '커피테이블',
    // table-coffee [1.322, 0.46, 0.8] → 6 × 2 × 4
    footprint: footprintOf([1.322, 0.46, 0.8]),
    rotSymmetry: 2,
    sizeClass: 'floor',
    recolorSlot: 'wood',
    tags: ['living'],
    // 거실 기본 가구. 이 그룹에서 가장 흔해도 되는 물건이다.
    weight: 6,
    defaultColorId: 5,
  },
  {
    id: 'table-coffee-square',
    modelId: 'table-coffee-square',
    name: '정사각 커피테이블',
    // table-coffee-square [0.8, 0.46, 0.8] → 4 × 2 × 4
    footprint: footprintOf([0.8, 0.46, 0.8]),
    // 정사각 상판 + 동일한 다리 네 개 + 사방 동일한 하부 가로대 → 90°가 무변화.
    // 4.2절이 지목한 "돌려도 아무 일도 안 일어나는" 소품이라 회전 동사가 잠긴다.
    rotSymmetry: 4,
    sizeClass: 'floor',
    recolorSlot: 'wood',
    // 정사각이라 복도 모서리에 놓아도 통행을 덜 막는다.
    tags: ['living', 'hall'],
    weight: 5,
    defaultColorId: 5,
  },
  {
    id: 'table-coffee-glass',
    modelId: 'table-coffee-glass',
    name: '유리 커피테이블',
    // table-coffee-glass [1.322, 0.46, 0.8] → 6 × 2 × 4
    footprint: footprintOf([1.322, 0.46, 0.8]),
    rotSymmetry: 2,
    sizeClass: 'floor',
    // 상판(glass)이 주된 색면. 금속 프레임을 칠하면 얇은 선만 바뀌어 색 변경이 안 보인다.
    recolorSlot: 'glass',
    tags: ['living'],
    weight: 3,
    defaultColorId: 9,
  },
  {
    id: 'table-coffee-glass-square',
    modelId: 'table-coffee-glass-square',
    name: '정사각 유리 커피테이블',
    // table-coffee-glass-square [0.8, 0.46, 0.8] → 4 × 2 × 4
    footprint: footprintOf([0.8, 0.46, 0.8]),
    // 정사각 유리 상판 + 사방 동일한 금속 프레임 → 90° 무변화.
    rotSymmetry: 4,
    sizeClass: 'floor',
    recolorSlot: 'glass',
    tags: ['living'],
    weight: 3,
    defaultColorId: 9,
  },
]

/* ------------------------------------------------------------------------------------------------
 * 책장 · 수납 - sizeClass 'anchor'
 *
 * 등을 벽에 붙이는 큰 가구다. 이 구간이 1인칭 수색의 **시야 차단물**을 담당한다 -
 * 뒤가 안 보이는 덩어리가 없으면 방은 한 번 둘러보는 것으로 끝난다.
 * ---------------------------------------------------------------------------------------------- */

const SHELVING: readonly CatalogEntryDraft[] = [
  {
    id: 'bookcase-open-low',
    modelId: 'bookcase-open-low',
    name: '낮은 오픈선반',
    // bookcase-open-low [0.8, 0.8, 0.5] → 4 × 4 × 2
    footprint: footprintOf([0.8, 0.8, 0.5]),
    // 네 귀퉁이 기둥 + 선반 두 장뿐이라 앞뒤가 똑같이 뚫려 있다 → 180° 무변화.
    // 기존 'bookcase-open'(4 × 8 × 2)의 절반 높이라 실루엣이 확실히 갈린다.
    rotSymmetry: 2,
    sizeClass: 'anchor',
    recolorSlot: 'wood',
    // 낮고 뚫려 있어 복도에 놓아도 시야를 막지 않는다 - 복도를 채울 몇 안 되는 수납이다.
    tags: ['living', 'bedroom', 'hall'],
    weight: 6,
    defaultColorId: 5,
  },
  {
    id: 'bookcase-doors',
    modelId: 'bookcase-closed-doors',
    name: '여닫이 책장',
    // bookcase-closed-doors [0.8, 1.7, 0.5] → 4 × 7 × 2
    footprint: footprintOf([0.8, 1.7, 0.5]),
    // 앞면만 문 두 짝이고 나머지 세 면은 판이다 → 네 방향이 전부 다르다.
    rotSymmetry: 1,
    sizeClass: 'anchor',
    // 문짝(wood)이 정면 전체를 덮는 색면. 손잡이(metal)는 면적이 없다.
    recolorSlot: 'wood',
    // 앞이 막혀 있어 1.7m 높이 전체가 시야 차단물이 된다 → 복도에 세워도 제 역할을 한다.
    tags: ['living', 'bedroom', 'hall'],
    weight: 5,
    defaultColorId: 5,
  },
  {
    id: 'cabinet-tv-doors',
    modelId: 'cabinet-television-doors',
    name: '여닫이 TV장',
    // cabinet-television-doors [1.6, 0.62, 0.52] → 7 × 3 × 3
    footprint: footprintOf([1.6, 0.62, 0.52]),
    // 정면에만 긴 문이 달렸다 → 1. 기존 'cabinet-tv'(개방형)와는 앞면이 뚫렸는지로 갈린다.
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'wood',
    tags: ['living', 'bedroom'],
    weight: 3,
    defaultColorId: 5,
  },
  {
    id: 'cardboard-box-open',
    modelId: 'cardboard-box-open',
    name: '열린 택배상자',
    // cardboard-box-open [0.744, 0.562, 0.425] → 3 × 3 × 2
    footprint: footprintOf([0.744, 0.562, 0.425]),
    // 뚜껑 한 짝은 위로 서 있고 다른 짝은 옆으로 젖혀져 있다 → 네 방향이 전부 다르다.
    rotSymmetry: 1,
    // 바닥에 굴러다니는 물건이지 벽에 기대는 가구가 아니다.
    sizeClass: 'floor',
    // 겉면(wood)이 골판지 색. woodDark는 안쪽 그늘이라 밖에서 거의 안 보인다.
    recolorSlot: 'wood',
    // 이사 안 끝난 집 느낌. 어느 구획에 있어도 이상하지 않아 복도를 싸게 채운다.
    tags: ['hall', 'bedroom', 'living', 'kitchen'],
    weight: 6,
    defaultColorId: 8,
  },
]

/* ------------------------------------------------------------------------------------------------
 * 주방 수납 - 하부장은 'anchor', 상부장은 'wall'
 *
 * 상부장을 'wall' 로 두는 것이 이 구간의 핵심이다. 벽 등급이 비면 배치기는 바닥만 채우고,
 * 그러면 눈높이(1.6m)에 아무것도 없는 방이 된다 - 1인칭에서 가장 많이 보는 높이가 텅 빈다.
 * ---------------------------------------------------------------------------------------------- */

const KITCHEN_STORAGE: readonly CatalogEntryDraft[] = [
  {
    id: 'kitchen-cabinet-corner',
    modelId: 'kitchen-cabinet-corner-round',
    name: '주방 코너장',
    // kitchen-cabinet-corner-round [0.9, 0.9, 0.9] → 4 × 4 × 4
    footprint: footprintOf([0.9, 0.9, 0.9]),
    // 정면이 사분원으로 둥글게 깎여 있고 상판에 손잡이 꼭지가 하나 있다 →
    // 점유 셀은 정사각(4 × 4)이지만 곡면이 한 방향을 가리키므로 1이다.
    rotSymmetry: 1,
    sizeClass: 'anchor',
    // 몸통(wood)이 곡면 전체. metal은 상판 테두리라 색을 바꿔도 선 하나만 변한다.
    recolorSlot: 'wood',
    tags: ['kitchen'],
    weight: 3,
    defaultColorId: 5,
  },
  {
    id: 'kitchen-cabinet-upper',
    modelId: 'kitchen-cabinet-upper',
    name: '주방 상부장',
    // kitchen-cabinet-upper [0.86, 0.78, 0.44] → 4 × 4 × 2
    footprint: footprintOf([0.86, 0.78, 0.44]),
    // 문이 정면 한 짝뿐이라 앞뒤가 다르다 → 1.
    rotSymmetry: 1,
    sizeClass: 'wall',
    recolorSlot: 'wood',
    tags: ['kitchen'],
    // 실제 주방은 상부장이 줄지어 붙는다 → 이 그룹에서 가장 흔해도 되는 벽 소품.
    weight: 7,
    defaultColorId: 8,
  },
  {
    id: 'kitchen-cabinet-upper-low',
    modelId: 'kitchen-cabinet-upper-low',
    name: '낮은 상부장',
    // kitchen-cabinet-upper-low [0.86, 0.39, 0.44] → 4 × 2 × 2
    footprint: footprintOf([0.86, 0.39, 0.44]),
    // 위로 젖히는 문 한 짝 + 가로 손잡이. 높이가 절반이라 상부장과 확실히 갈린다.
    rotSymmetry: 1,
    sizeClass: 'wall',
    recolorSlot: 'wood',
    // 후드 위·복도 상단처럼 천장 가까운 자투리를 메우는 용도라 복도에도 허용한다.
    tags: ['kitchen', 'hall'],
    weight: 5,
    defaultColorId: 8,
  },
  {
    id: 'kitchen-cabinet-upper-corner',
    modelId: 'kitchen-cabinet-upper-corner',
    name: '코너 상부장',
    // kitchen-cabinet-upper-corner [0.43, 0.78, 0.42] → 2 × 4 × 2
    footprint: footprintOf([0.43, 0.78, 0.42]),
    // 문 없이 선반이 사분원으로 깎여 밖으로 열린 코너 선반이다 →
    // 열린 사분면이 한 방향을 가리키므로 정사각 점유(2 × 2)여도 1이다.
    rotSymmetry: 1,
    sizeClass: 'wall',
    // 단일 슬롯 모델이라 선택지가 하나뿐이다.
    recolorSlot: 'wood',
    tags: ['kitchen', 'hall'],
    weight: 4,
    defaultColorId: 8,
  },
]

/* ------------------------------------------------------------------------------------------------
 * 욕실 수납 · 조명
 * ---------------------------------------------------------------------------------------------- */

const BATH_AND_LAMP: readonly CatalogEntryDraft[] = [
  {
    id: 'bathroom-cabinet',
    modelId: 'bathroom-cabinet',
    name: '욕실 거울장',
    // bathroom-cabinet [0.46, 0.78, 0.26] → 2 × 4 × 2
    footprint: footprintOf([0.46, 0.78, 0.26]),
    // 정면이 거울문, 뒷면은 맨 판이다 → 1.
    rotSymmetry: 1,
    sizeClass: 'wall',
    // 거울문을 두른 나무 프레임이 정면에서 보이는 색면이다.
    recolorSlot: 'wood',
    // 거울 달린 벽 수납은 복도 끝에도 흔하다. 복도의 눈높이를 채울 몇 안 되는 소품.
    tags: ['bath', 'hall'],
    weight: 6,
    defaultColorId: 8,
  },
  {
    id: 'bathroom-vanity',
    modelId: 'bathroom-cabinet-drawer',
    name: '세면 수납장',
    // bathroom-cabinet-drawer [0.86, 0.944, 0.64] → 4 × 4 × 3
    footprint: footprintOf([0.86, 0.944, 0.64]),
    // 수도꼭지가 뒤쪽에, 서랍 두 칸이 앞쪽에 있다 → 네 방향이 전부 다르다.
    rotSymmetry: 1,
    // 세면대가 얹힌 하부장. 1m가 안 되는 높이라 시야를 막지 못하므로 anchor가 아니라 floor.
    sizeClass: 'floor',
    // 몸통(wood)이 가장 큰 색면. carpetWhite는 도기 세면볼이라 흰색으로 두는 편이 읽힌다.
    recolorSlot: 'wood',
    tags: ['bath'],
    weight: 5,
    defaultColorId: 5,
  },
  {
    id: 'lamp-square-table',
    modelId: 'lamp-square-table',
    name: '사각 탁상등',
    // lamp-square-table [0.24, 0.58, 0.24] → 1 × 3 × 1
    footprint: footprintOf([0.24, 0.58, 0.24]),
    // 정사각 갓 + 정사각 받침 + 가운데 기둥. 90°를 돌려도 화면에 아무 일도 없다 →
    // 4.2절에 따라 회전 동사를 잠근다(정사각 점유 1 × 1 이라 규약도 만족).
    rotSymmetry: 4,
    // 바닥이 아니라 협탁·책상 위에 선다. 이 그룹이 만든 상판을 되받는 소품이다.
    sizeClass: 'surface',
    // 갓(lamp)이 이 물건의 전부다. 기둥(metal)은 두께가 한 셀도 안 된다.
    recolorSlot: 'lamp',
    tags: ['living', 'bedroom', 'hall'],
    weight: 5,
    defaultColorId: 9,
  },
]

/* ------------------------------------------------------------------------------------------------
 * 통합 export
 * ---------------------------------------------------------------------------------------------- */

/**
 * 테이블·수납 그룹 20종. 통합 담당은 **이 심볼 하나만** import한다.
 *
 * 구간을 나눠 선언한 뒤 여기서 합치는 이유: 구간별 주석(왜 floor인가 / 왜 wall인가)이
 * 엔트리와 붙어 있어야 나중에 값을 고칠 때 근거가 같이 보인다.
 *
 * ### SKIPPED - 담당 35종 중 배선하지 않은 15종과 그 이유
 *
 * | modelId | 이유 |
 * |---|---|
 * | `desk` | 기존 `desk` 엔트리가 이미 이 GLB를 쓴다 |
 * | `side-table-drawers` | 기존 `side-table` 엔트리가 이미 쓴다 |
 * | `lamp-round-table` | 기존 `lamp-table` 엔트리가 이미 쓴다 |
 * | `bookcase-open` | 기존 `bookcase-open` 엔트리가 이미 쓴다 |
 * | `bookcase-closed-wide` | 기존 `wardrobe` 엔트리가 이미 쓴다 |
 * | `cabinet-television` | 기존 `cabinet-tv` 엔트리가 이미 쓴다 |
 * | `cardboard-box-closed` | 기존 `cardboard-box` 엔트리가 이미 쓴다 |
 * | `kitchen-cabinet-drawer` | 기존 `kitchen-cabinet` 엔트리가 이미 쓴다 |
 * | `table` | 매니페스트 `aliasOf: table-dining` - 기존 `table-dining`과 **바이트 동일 파일** |
 * | `bookcase-closed` | 매니페스트 `aliasOf: shelf-tall` - 기존 `shelf-tall`과 **바이트 동일 파일** |
 * | `side-table` | `side-table-drawers`와 다리·상판·가로대가 같고 서랍 앞판만 없다. 외곽선 동일 |
 * | `table-cross-cloth` | `table-cross`(같은 X다리) + `table-cloth`(같은 러너)의 조합. 두 축 모두 이미 있다 |
 * | `kitchen-cabinet` | `kitchen-cabinet-drawer`와 같은 4 × 4 × 4 상판 상자. 문/서랍 앞판만 다르다 |
 * | `kitchen-cabinet-corner-inner` | 위와 같은 4 × 4 × 4 상자. 바닥 앞쪽 홈은 몇 미터 밖에서 사라진다 |
 * | `kitchen-cabinet-upper-double` | `kitchen-cabinet-upper`와 같은 4 × 4 × 2 상자. 문짝 개수만 다르다 |
 */
export const TABLES_STORAGE_ENTRIES: readonly CatalogEntryDraft[] = [
  ...DINING_TABLES,
  ...COFFEE_TABLES,
  ...SHELVING,
  ...KITCHEN_STORAGE,
  ...BATH_AND_LAMP,
]
